import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Container,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Phone as PhoneIcon, 
  Email as EmailIcon,
  Home as HomeIcon,
  Hotel as HotelIcon,
  Search as SearchIcon,
  ContactMail as ContactMailIcon,
  EventAvailable as EventAvailableIcon
} from '@mui/icons-material';
import { getImageUrl } from '../services/api';

const hotelLogoUrl = getImageUrl('GrosvenorLogo.png', 'logos');

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [thingsToDoAnchorEl, setThingsToDoAnchorEl] = useState(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const primaryNavItems = [
    { name: 'Home', path: '/' },
    { name: 'Rooms', path: '/rooms' },
    { name: 'About Us', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Restaurant', path: '/restaurant' },
    { name: 'Blog', path: '/blogs' },
    { name: 'Things To Do', path: '#things-to-do' },
    { name: 'More', path: '#more' },
    { name: 'Contact', path: '/contact' },
  ];

  const thingsToDoItems = [
    { name: 'Food and Drink', path: '/food-and-drink' },
    { name: 'Shopping', path: '/shopping' },
    { name: "Plymouth\'s Highlights", path: '/plymouth-highlights' },
    { name: 'Nearby Attractions', path: '/nearby-attractions' },
  ];

  const moreItems = [
    { name: 'Rules & Regulation', path: '/rules-and-regulation' },
    { name: 'Getting to the Grosvenor Hotel Plymouth', path: '/getting-to-the-grosvenor-hotel-plymouth' },
  ];

  const mobileNavItems = [
    ...primaryNavItems.slice(0, 6),
    ...thingsToDoItems,
    ...moreItems,
    primaryNavItems[8],
  ];

  const mobileBottomNavItems = [
    { name: 'Home', path: '/', icon: HomeIcon },
    { name: 'Rooms', path: '/rooms', icon: HotelIcon },
    { name: 'Search', path: '/search', icon: SearchIcon },
    { name: 'Contact', path: '/contact', icon: ContactMailIcon },
    { name: 'Book', path: '/reservation', icon: EventAvailableIcon },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => location.pathname === path;
  const isMenuActive = (items) => items.some((item) => isActive(item.path));

  const handleThingsToDoOpen = (event) => {
    setThingsToDoAnchorEl(event.currentTarget);
  };

  const handleMoreOpen = (event) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleThingsToDoClose = () => {
    setThingsToDoAnchorEl(null);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Box
          component="img"
          src={hotelLogoUrl}
          alt="Grosvenor Plymouth"
          sx={{ height: 52, width: 'auto', backgroundColor: 'transparent', objectFit: 'contain' }}
        />
      </Box>
      <List>
        {mobileNavItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemText 
              primary={
                <Link 
                  to={item.path} 
                  style={{ 
                    textDecoration: 'none', 
                    color: isActive(item.path) ? theme.palette.primary.main : 'inherit',
                    fontWeight: isActive(item.path) ? 600 : 400
                  }}
                >
                  {item.name}
                </Link>
              } 
            />
          </ListItem>
        ))}
        <ListItem disablePadding>
          <Button
            component={Link}
            to="/reservation"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mx: 2, mt: 2 }}
          >
            Book Now
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Top Contact Bar */}
      <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', py: 1 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">+92 324 5405650</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">info@grosvenor-plymouth.com</Typography>
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography variant="caption">7-11 Elliot Street, The Hoe, Plymouth, PL1 2PP</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Navigation */}
      <AppBar position="sticky" sx={{ background: 'linear-gradient(to right, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <Container maxWidth="xl">
          <Toolbar>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1.5 }}>
              <Box
                component={Link}
                to="/"
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <Box
                  component="img"
                  src={hotelLogoUrl}
                  alt="Grosvenor Plymouth"
                  sx={{
                    height: { xs: 42, md: 52 },
                    width: 'auto',
                    backgroundColor: 'transparent',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              {primaryNavItems.slice(0, 6).map((item) => (
                <Button
                  key={item.name}
                  component={Link}
                  to={item.path}
                  sx={{
                    fontWeight: isActive(item.path) ? 700 : 500,
                    color: isActive(item.path) ? '#667eea' : '#374151',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: isActive(item.path) ? '80%' : '0%',
                      height: '3px',
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '2px',
                      transition: 'width 0.3s ease'
                    },
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#667eea',
                      '&::after': {
                        width: '80%'
                      }
                    }
                  }}
                >
                  {item.name}
                </Button>
              ))}
              <Button
                onClick={handleThingsToDoOpen}
                sx={{
                  fontWeight: isMenuActive(thingsToDoItems) ? 700 : 500,
                  color: isMenuActive(thingsToDoItems) ? '#667eea' : '#374151',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: isMenuActive(thingsToDoItems) ? '80%' : '0%',
                    height: '3px',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '2px',
                    transition: 'width 0.3s ease'
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#667eea',
                    '&::after': {
                      width: '80%'
                    }
                  }
                }}
              >
                Things To Do
              </Button>
              <Menu
                anchorEl={thingsToDoAnchorEl}
                open={Boolean(thingsToDoAnchorEl)}
                onClose={handleThingsToDoClose}
                MenuListProps={{ onMouseLeave: handleThingsToDoClose }}
              >
                {thingsToDoItems.map((item) => (
                  <MenuItem
                    key={item.name}
                    component={Link}
                    to={item.path}
                    onClick={handleThingsToDoClose}
                    selected={isActive(item.path)}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </Menu>
              <Button
                onClick={handleMoreOpen}
                sx={{
                  fontWeight: isMenuActive(moreItems) ? 700 : 500,
                  color: isMenuActive(moreItems) ? '#667eea' : '#374151',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: isMenuActive(moreItems) ? '80%' : '0%',
                    height: '3px',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '2px',
                    transition: 'width 0.3s ease'
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#667eea',
                    '&::after': {
                      width: '80%'
                    }
                  }
                }}
              >
                More
              </Button>
              <Menu
                anchorEl={moreAnchorEl}
                open={Boolean(moreAnchorEl)}
                onClose={handleMoreClose}
                MenuListProps={{ onMouseLeave: handleMoreClose }}
              >
                {moreItems.map((item) => (
                  <MenuItem
                    key={item.name}
                    component={Link}
                    to={item.path}
                    onClick={handleMoreClose}
                    selected={isActive(item.path)}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </Menu>
              <Button
                component={Link}
                to="/contact"
                sx={{
                  fontWeight: isActive('/contact') ? 700 : 500,
                  color: isActive('/contact') ? '#667eea' : '#374151',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: isActive('/contact') ? '80%' : '0%',
                    height: '3px',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '2px',
                    transition: 'width 0.3s ease'
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#667eea',
                    '&::after': {
                      width: '80%'
                    }
                  }
                }}
              >
                Contact
              </Button>
              <Button
                component={Link}
                to="/reservation"
                variant="contained"
                sx={{ 
                  ml: 2,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  fontWeight: 700,
                  px: 3,
                  borderRadius: 2,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 20px rgba(16, 185, 129, 0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Book Now
              </Button>
            </Box>

            {/* Mobile menu button */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: '#667eea',
                  backgroundColor: mobileOpen ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.15)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          px: 1,
          py: 0.75,
          backgroundColor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(14px)',
          borderTop: '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.08)',
          justifyContent: 'space-between',
        }}
      >
        {mobileBottomNavItems.map((item) => {
          const active = item.path === '/search'
            ? location.pathname === '/search'
            : isActive(item.path);

          return (
            <Button
              key={item.name}
              component={Link}
              to={item.path}
              sx={{
                minWidth: 0,
                flex: 1,
                color: active ? '#2563eb' : '#64748b',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.35,
                py: 0.6,
                borderRadius: 2.5,
                textTransform: 'none',
                fontSize: '0.68rem',
                fontWeight: active ? 700 : 600,
                backgroundColor: active ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                },
              }}
            >
              <item.icon sx={{ fontSize: 20 }} />
              <Box component="span">{item.name}</Box>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default Navbar;
